import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/** Substitui o flash() da versão anterior: mensagens rápidas no canto da tela. */
interface Aviso {
  id: number;
  texto: string;
  erro: boolean;
}

interface ContextoAvisos {
  avisar: (texto: string) => void;
  avisarErro: (erro: unknown) => void;
}

const Contexto = createContext<ContextoAvisos | null>(null);
let proximoId = 1;

export function AvisosProvider({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const remover = useCallback((id: number) => setAvisos((lista) => lista.filter((a) => a.id !== id)), []);

  const adicionar = useCallback(
    (texto: string, erro: boolean) => {
      const id = proximoId++;
      setAvisos((lista) => [...lista, { id, texto, erro }]);
      setTimeout(() => remover(id), erro ? 8000 : 5000);
    },
    [remover],
  );

  const valor = useMemo<ContextoAvisos>(
    () => ({
      avisar: (texto) => adicionar(texto, false),
      avisarErro: (erro) => adicionar(erro instanceof Error ? erro.message : String(erro), true),
    }),
    [adicionar],
  );

  return (
    <Contexto.Provider value={valor}>
      {children}
      <div className="avisos no-print" role="status" aria-live="polite">
        {avisos.map((a) => (
          <div key={a.id} className={`flash${a.erro ? " erro" : ""}`} onClick={() => remover(a.id)}>
            {a.texto}
          </div>
        ))}
      </div>
    </Contexto.Provider>
  );
}

export function useAvisos(): ContextoAvisos {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error("useAvisos fora do AvisosProvider");
  return ctx;
}
